import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useHistory} from 'react-router-dom'
import {
    Box,
    Flex,
    Text,
    Skeleton as ChakraSkeleton,
    useBreakpointValue,
    VisuallyHidden
} from '@chakra-ui/react'
import {PRODUCT_TILE_IMAGE_TYPE} from '../../../constants'
import Swatch from '../../../components/swatch-group/swatch'
import {useColorVariation, useVariationParams} from '../../../hooks'
import {usePDPSearchParams} from '../../../hooks/use-pdp-search-params'
import {useMaterialVariation} from '../../../hooks/use-variation-attributes'
import {findImageGroupBy} from '../../../utils/image-groups-utils'
import {getMasterProductId} from '../../../utils/product-utils'
import MaterialVariationSelection from '../../../components/material-variation-selection'
import Slider from '../../../components/slider'
import DynamicImage from '../../../components/dynamic-image'

const ProductSwatch = ({imageProps, product, preload, variation, variationValue, visible}) => {
    const history = useHistory()
    const variationParams = useVariationParams(product, true)
    const imageGroup = useMemo(
        () =>
            findImageGroupBy(product.imageGroups, {
                viewType: PRODUCT_TILE_IMAGE_TYPE,
                selectedVariationAttributes: {
                    ...variationParams,
                    [variation.id]: variationValue.value
                }
            }),
        [product, variationParams, variation, variationValue]
    )

    return (
        <Swatch
            key={variationValue.value}
            value={variationValue.value}
            name={variationValue.name}
            href={variationValue.href}
            disabled={!variationValue.orderable}
            selected={variation.selectedValue.value === variationValue.value}
            size="auto"
            variant="square"
            onChange={(_value, href) => {
                if (!href) return
                history.replace(href)
            }}
        >
            {imageGroup?.images?.[0] ? (
                <DynamicImage
                    height="100%"
                    width="100%"
                    minWidth="24px"
                    minHeight="24px"
                    preload={preload}
                    aria-hidden={!visible}
                    {...imageProps}
                    imageProps={{
                        alt: imageGroup.images[0].alt,
                        ...imageProps.imageProps
                    }}
                    src={`${
                        imageGroup.images[0].disBaseLink || imageGroup.images[0].link
                    }[?sw={width}&q=60]`}
                />
            ) : variationValue.image ? (
                <Box
                    height="100%"
                    width="100%"
                    minWidth="24px"
                    minHeight="24px"
                    backgroundRepeat="no-repeat"
                    backgroundSize="cover"
                    backgroundColor={variationValue.name.toLowerCase()}
                    backgroundImage={
                        variationValue.image
                            ? `url("${encodeURI(
                                  variationValue.image.disBaseLink || variationValue.image.link
                              )}")`
                            : ''
                    }
                />
            ) : (
                variationValue.name
            )}
        </Swatch>
    )
}

ProductSwatch.propTypes = {
    imageProps: PropTypes.object,
    preload: PropTypes.bool,
    product: PropTypes.object,
    variation: PropTypes.object,
    variationValue: PropTypes.object,
    visible: PropTypes.bool
}

const CustomizablePsdpSetProductTileWithThumbnails = ({product}) => {
    const history = useHistory()
    const [allParams, productParams] = usePDPSearchParams(product.setProductId)
    const {c_productShortName} = product

    // compute if different master/variant is loading to show appropriate loading state
    const [, isMasterLoading] = useMemo(() => {
        const params = productParams
        const wantedPid = params.get('pid') || product?.id
        return [
            // variant is loading when different pid is selected than the loaded one
            wantedPid !== product?.id && wantedPid !== getMasterProductId(product),
            // master is loading when different pid is selected than the loaded one
            wantedPid !== product?.id &&
                wantedPid !== getMasterProductId(product) &&
                // and the pid is not in the variants list
                !product.variants?.find((variant) => variant.productId === wantedPid)
        ]
    }, [product, productParams])

    const onChangedMaterial = useCallback(
        (value) => {
            // delete the selected variation attributes when changing material, as they rarely match, but break useVariationParams
            product?.variationAttributes.forEach((variant) => {
                productParams.delete(variant.id)
            })
            productParams.set('pid', value)
            allParams.set(product?.setProductId, productParams.toString())

            history.replace({search: allParams.toString()})
        },
        [product, productParams, allParams, history]
    )

    /* Get extended variation attributes data with images etc. */
    const materialVariation = useMaterialVariation(product)
    const colorVariation = useColorVariation(product, true)

    const {scroll: itemsPerScroll, slide: itemsPerSlide} = useBreakpointValue({
        base: {scroll: 2, slide: 2.5},
        md: {scroll: 4, slide: 4.5},
        lg: {scroll: 2, slide: 2.5}
    })

    const initialColorIndex = useMemo(
        () =>
            colorVariation?.values.findIndex((v) => v.value === colorVariation.selectedValue.value),
        [colorVariation]
    )

    if (!materialVariation && !colorVariation) {
        // we don't have anything to display when no variations are present
        return null
    }

    return (
        <Flex flexDirection="column" gap={2}>
            <Flex flexDirection="column" gap={4}>
                {/*Material*/}
                <MaterialVariationSelection
                    hideTooltop
                    product={product}
                    onChange={onChangedMaterial}
                    variant="thumbnails"
                />

                {/* color */}
                {colorVariation ? (
                    <Box as="fieldset" role="radiogroup" minWidth={0}>
                        <VisuallyHidden as="legend">{colorVariation.name}</VisuallyHidden>
                        <Slider
                            disableInvisible={false}
                            header={
                                <Flex
                                    alignItems="center"
                                    minHeight={8}
                                    paddingRight="5.75rem"
                                    paddingBottom={1}
                                    marginBottom={2}
                                >
                                    <Text textStyle="Regular Small">
                                        <Text as="span" textStyle="Semi Bold Small">
                                            {c_productShortName
                                                ? c_productShortName + ' ' + colorVariation.name
                                                : colorVariation.name}
                                        </Text>
                                        <Text as="span" textStyle="Semi Bold Small">
                                            :
                                        </Text>
                                        <Text as="span"> </Text>
                                        <Text as="span">{colorVariation.selectedValue.name}</Text>
                                    </Text>
                                </Flex>
                            }
                            hidePagination
                            initialIndex={initialColorIndex}
                            itemsContainerHtmlProps={{
                                // stretch the slider to the edge of the screen but
                                // pad the content inside to match the layout
                                marginX: {base: -4, lg: 0},
                                paddingX: {base: 4, lg: 0}
                            }}
                            itemsHtmlProps={{
                                // need to move the snap position to take into account the padding
                                scrollMarginX: {base: 4, lg: 0}
                            }}
                            itemsPerScroll={itemsPerScroll}
                            itemsPerSlide={itemsPerSlide}
                            loading={isMasterLoading}
                            variant="productsSwatchGroup"
                        >
                            {isMasterLoading ? (
                                <>
                                    <ChakraSkeleton width="150px" height="150px" />
                                    <ChakraSkeleton width="150px" height="150px" />
                                    <ChakraSkeleton width="150px" height="150px" />
                                </>
                            ) : (
                                colorVariation.values.map((v) => (
                                    <ProductSwatch
                                        key={v.value}
                                        imageProps={{
                                            imageProps: {
                                                objectFit: 'cover'
                                            },
                                            widths: [150]
                                        }}
                                        product={product}
                                        value={v.value}
                                        variation={colorVariation}
                                        variationValue={v}
                                    />
                                ))
                            )}
                        </Slider>
                    </Box>
                ) : null}
            </Flex>
        </Flex>
    )
}

CustomizablePsdpSetProductTileWithThumbnails.propTypes = {
    /**
     * The set product that will be represented in this component.
     */
    product: PropTypes.object
}

export default CustomizablePsdpSetProductTileWithThumbnails
